import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useQueryVerifiedContactCardByProjectId } from 'modules/contactCards/hooks/queries';
import {
  selectIsGlobalSmsFirstPartyUIFeatureFlagOn,
  selectIsSendMmsOn,
  selectIsSMSContactCardUIOn,
} from 'core/userContext';
import { ContactCardContextProvider } from 'modules/contactCards/contexts/ContactCardContextProvider';
import { BaseAssetManagerContextProvider } from 'modules/assetManager/contexts/BaseAssetManagerContextProvider';
import {
  useCreateVerifiedContactCard,
  useUpdateVerifiedContactCard,
  useDeleteVerifiedContactCard,
} from 'modules/contactCards/hooks/mutations';
import { useSmsSendersWithESP } from 'modules/templateEditor/hooks/useSmsSendersWithESP';
import { useContactCardContext } from 'modules/contactCards/contexts/ContactCardContext';
import { SMSContentDefinition } from 'modules/graphql/types';
import { ContactCard } from 'modules/contactCards/types';
import { FormField } from 'ui/formField';
import { Tooltip } from 'ui/tooltip';
import { InfoBanner } from 'ui/banner';
import { Edit16 } from 'ui/icons';
import { Button, BUTTON_VARIANTS } from 'ui/button';
import { spacing } from 'ui/theme';
import { BasicSelect } from 'ui/selects';
import { CheckboxField } from 'modules/templateEditor/common';
import { ManageContactCardModal } from 'modules/contactCards/components/ManageContactCardModal';
import { formatContactCardOptions, validateContactCard } from 'modules/contactCards/utils';
import { SettingsReadOnlyText } from '../../common/EditorContentContainer.styled';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { SETTINGS_DISPLAY_CONTENT } from '../constants';

const CONTACT_CARD_DISPLAY_CONTENT = {
  checkboxLabel: 'Send an SMS contact card with this message',
  contactDropdownLabel: 'Select contact card',
  createCardsLabel: 'Create contact card',
  editCardsLabel: 'Manage contact cards',
  infoBannerMessage:
    'This contact card will only send to users with US and Canadian phone numbers.',
  labelText: 'Contact cards',
};

const ContactCardsFormSectionWrapper = styled.div`
  [data-test='sms-create-contact-button'],
  [data-test='basic-select-sms-contact-cards-select'] {
    margin-top: ${spacing.half};
    margin-left: ${spacing.plusHalf};
  }
  [data-test='basic-select-sms-contact-cards-select'] {
    width: 95%;
  }
  [data-test='sms-contact-card-form-field'] {
    margin-top: ${spacing.plusHalf};
  }
  [data-test='sms-manage-contact-button'] {
    margin-top: ${spacing.half};

    & > svg {
      margin-right: ${spacing.half};
    }
  }
  [data-test='basic-select-value-display'] > div {
    max-width: 280px;
    text-overflow: ellipsis;
  }
`;

const FieldContents = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const GlobalSmsInfoBanner = styled(InfoBanner)`
  margin-top: ${spacing.threeQuarter};
`;

const withContext = (Component: FC) => (props: any) => {
  const { initialTemplate } = useEditableFieldsContext();
  const currentContactId = (initialTemplate.mediumData as SMSContentDefinition)
    ?.verifiedContactCardId;
  return (
    <BaseAssetManagerContextProvider
      // WARNING: This likely violates the rules of hooks
      assetSource={useQueryVerifiedContactCardByProjectId}
      assetTypeName="Contact card"
      validateAsset={validateContactCard}
      createAssetHook={useCreateVerifiedContactCard}
      updateAssetHook={useUpdateVerifiedContactCard}
      deleteAssetHook={useDeleteVerifiedContactCard}
      initialSelectedId={currentContactId}
    >
      <ContactCardContextProvider>
        <Component {...props} />
      </ContactCardContextProvider>
    </BaseAssetManagerContextProvider>
  );
};

export const SMSContactCardFormSection = withContext(() => {
  const smsContactCardEnabled = useSelector(selectIsSMSContactCardUIOn);
  const sendMmsEnabled = useSelector(selectIsSendMmsOn);
  const isGlobalSmsEnabled = useSelector(selectIsGlobalSmsFirstPartyUIFeatureFlagOn);
  const smsSenders = useSmsSendersWithESP();
  const { isEditMode, initialTemplate, liveTemplate, setCurrentMediumField } =
    useEditableFieldsContext();
  const smsData = (liveTemplate?.mediumData || {}) as SMSContentDefinition;
  const currentContactId = (initialTemplate.mediumData as SMSContentDefinition)
    ?.verifiedContactCardId;
  const selectedContactId = smsData?.verifiedContactCardId;
  const isMediaUrlSet = !!smsData?.imageUrl;
  const isSenderFirstParty = !!smsSenders[smsData.smsSender.id]?.esp?.isSenderIterable;
  const isDisabled = isMediaUrlSet || (!sendMmsEnabled && isSenderFirstParty);

  const [contactCardsEnabled, setContactCardsEnabled] = useState<boolean>(!!currentContactId);
  const [showInfoBanner, setShowInfoBanner] = useState(
    isGlobalSmsEnabled && typeof currentContactId === 'number',
  );

  const { assets: contactCards, isModalOpen, setIsModalOpen } = useContactCardContext();
  const formattedOptions = useMemo(() => formatContactCardOptions(contactCards), [contactCards]);

  const savedContactCard = useMemo(
    () => contactCards.find((card: ContactCard) => card.id === currentContactId),
    [currentContactId, contactCards],
  );

  const handleCheckboxClick = useCallback(() => {
    if (contactCardsEnabled) {
      setCurrentMediumField('verifiedContactCardId', undefined);
      setShowInfoBanner(false);
    } else {
      // We set it to -1 so that media url knows this has been selected and that it should be disabled
      setCurrentMediumField('verifiedContactCardId', currentContactId || -1);
      setShowInfoBanner(isGlobalSmsEnabled);
    }
    setContactCardsEnabled(!contactCardsEnabled);
  }, [contactCardsEnabled]);

  useEffect(() => {
    setContactCardsEnabled(!!currentContactId);
  }, [isEditMode]);

  const renderEditMode = () => (
    <Tooltip
      isEnabled={isDisabled}
      tooltipContent={
        isMediaUrlSet
          ? SETTINGS_DISPLAY_CONTENT.disabledContactCard
          : SETTINGS_DISPLAY_CONTENT.MmsNotAvailable
      }
    >
      <CheckboxField
        label={CONTACT_CARD_DISPLAY_CONTENT.checkboxLabel}
        status={contactCardsEnabled}
        onClick={handleCheckboxClick}
        isDisabled={isDisabled}
      />
      <FieldContents data-test="sms-contact-cards-contents">
        {contactCardsEnabled &&
          (formattedOptions ? (
            <>
              <BasicSelect
                id="sms-contact-cards-select"
                options={formattedOptions}
                placeholder="Select a contact card..."
                onSelect={([selected]: number[]) => {
                  setCurrentMediumField('verifiedContactCardId', selected);
                }}
                defaultValues={[selectedContactId as number]}
                isDisabled={isDisabled}
              />
              <Button
                data-test="sms-manage-contact-button"
                variant={BUTTON_VARIANTS.GHOST}
                onClick={() => setIsModalOpen(true)}
                isDisabled={isDisabled}
              >
                <Edit16 />
                {CONTACT_CARD_DISPLAY_CONTENT.editCardsLabel}
              </Button>
            </>
          ) : (
            <Button
              data-test="sms-create-contact-button"
              variant={BUTTON_VARIANTS.SECONDARY}
              onClick={() => setIsModalOpen(true)}
              isDisabled={isDisabled}
            >
              {CONTACT_CARD_DISPLAY_CONTENT.createCardsLabel}
            </Button>
          ))}
        {showInfoBanner && (
          <GlobalSmsInfoBanner>
            {CONTACT_CARD_DISPLAY_CONTENT.infoBannerMessage}
          </GlobalSmsInfoBanner>
        )}
      </FieldContents>
    </Tooltip>
  );

  return smsContactCardEnabled ? (
    <ContactCardsFormSectionWrapper>
      <FormField
        data-test="sms-contact-card-form-field"
        isHorizontalLayout
        labelText={CONTACT_CARD_DISPLAY_CONTENT.labelText}
      >
        {isEditMode ? (
          renderEditMode()
        ) : (
          <SettingsReadOnlyText isUnset={!savedContactCard}>
            {savedContactCard?.displayName || 'None'}
          </SettingsReadOnlyText>
        )}
      </FormField>
      {isModalOpen && <ManageContactCardModal />}
    </ContactCardsFormSectionWrapper>
  ) : null;
});
